<template>
  <div class="operation-log">
    <div class="header">
      <h2>操作日志</h2>
      <div class="actions">
        <el-button @click="refreshData">
          <el-icon><Refresh /></el-icon>
          刷新
        </el-button>
      </div>
    </div>

    <!-- 搜索表单 -->
    <el-card class="search-card">
      <el-form :model="searchForm" label-width="80px" inline>
        <el-form-item label="操作类型">
          <el-select v-model="searchForm.actionType" placeholder="请选择操作类型" clearable style="width: 150px">
            <el-option label="全部" value="" />
            <el-option label="登录" value="login" />
            <el-option label="登出" value="logout" />
            <el-option label="创建" value="create" />
            <el-option label="更新" value="update" />
            <el-option label="删除" value="delete" />
            <el-option label="查看" value="view" />
            <el-option label="审核" value="audit" />
          </el-select>
        </el-form-item>
        <el-form-item label="操作模块">
          <el-select v-model="searchForm.module" placeholder="请选择模块" clearable style="width: 150px">
            <el-option label="全部" value="" />
            <el-option label="用户管理" value="user" />
            <el-option label="债务管理" value="debt" />
            <el-option label="商户管理" value="merchant" />
            <el-option label="任务管理" value="task" />
            <el-option label="积分管理" value="credit" />
            <el-option label="交易管理" value="transaction" />
            <el-option label="系统设置" value="system" />
          </el-select>
        </el-form-item>
        <el-form-item label="操作人">
          <el-input v-model="searchForm.operator" placeholder="请输入操作人" clearable style="width: 150px" />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">
            <el-icon><Search /></el-icon>
            搜索
          </el-button>
          <el-button @click="resetSearch">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 日志列表 -->
    <el-card>
      <el-table :data="logList" v-loading="loading" stripe border>
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="module" label="操作模块" width="120">
          <template #default="{ row }">
            <el-tag size="small" :type="getModuleTag(row.module)">{{ getModuleText(row.module) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="actionType" label="操作类型" width="100">
          <template #default="{ row }">
            <el-tag size="small" :type="getActionTag(row.actionType)">{{ getActionText(row.actionType) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="description" label="操作描述" min-width="250" />
        <el-table-column prop="operatorName" label="操作人" width="120" />
        <el-table-column prop="operatorIp" label="IP地址" width="140" />
        <el-table-column prop="status" label="状态" width="80">
          <template #default="{ row }">
            <el-tag :type="row.status === 'success' ? 'success' : 'danger'" size="small">
              {{ row.status === 'success' ? '成功' : '失败' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createdAt" label="操作时间" width="180">
          <template #default="{ row }">
            {{ formatDateTime(row.createdAt) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="100" fixed="right">
          <template #default="{ row }">
            <el-button type="primary" size="small" @click="handleViewDetail(row)">详情</el-button>
          </template>
        </el-table-column>
      </el-table>
      
      <!-- 分页 -->
      <div class="pagination">
        <el-pagination
          v-model:current-page="pagination.current"
          v-model:page-size="pagination.size"
          :total="pagination.total"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>

    <!-- 详情对话框 -->
    <el-dialog v-model="detailDialogVisible" title="操作日志详情" width="600px">
      <el-descriptions :column="2" border v-if="currentLog">
        <el-descriptions-item label="日志ID">{{ currentLog.id }}</el-descriptions-item>
        <el-descriptions-item label="状态">
          <el-tag :type="currentLog.status === 'success' ? 'success' : 'danger'">
            {{ currentLog.status === 'success' ? '成功' : '失败' }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="操作模块">
          <el-tag :type="getModuleTag(currentLog.module)">{{ getModuleText(currentLog.module) }}</el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="操作类型">
          <el-tag :type="getActionTag(currentLog.actionType)">{{ getActionText(currentLog.actionType) }}</el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="操作描述" :span="2">{{ currentLog.description }}</el-descriptions-item>
        <el-descriptions-item label="操作人">{{ currentLog.operatorName }}</el-descriptions-item>
        <el-descriptions-item label="操作人ID">{{ currentLog.operatorId }}</el-descriptions-item>
        <el-descriptions-item label="IP地址">{{ currentLog.operatorIp }}</el-descriptions-item>
        <el-descriptions-item label="浏览器">{{ currentLog.browser || '未知' }}</el-descriptions-item>
        <el-descriptions-item label="请求URL" :span="2">{{ currentLog.requestUrl }}</el-descriptions-item>
        <el-descriptions-item label="请求方法">{{ currentLog.requestMethod }}</el-descriptions-item>
        <el-descriptions-item label="操作时间">{{ formatDateTime(currentLog.createdAt) }}</el-descriptions-item>
        <el-descriptions-item label="请求参数" :span="2">
          <el-input type="textarea" :rows="3" :model-value="formatJson(currentLog.requestData)" readonly />
        </el-descriptions-item>
        <el-descriptions-item label="响应结果" :span="2" v-if="currentLog.responseData">
          <el-input type="textarea" :rows="3" :model-value="formatJson(currentLog.responseData)" readonly />
        </el-descriptions-item>
        <el-descriptions-item label="错误信息" :span="2" v-if="currentLog.errorMessage">
          <el-text type="danger">{{ currentLog.errorMessage }}</el-text>
        </el-descriptions-item>
      </el-descriptions>
      <template #footer>
        <el-button @click="detailDialogVisible = false">关闭</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import { Refresh, Search } from '@element-plus/icons-vue'
import { logApi } from '@/api/modules/log.js'

const loading = ref(false)
const detailDialogVisible = ref(false)
const currentLog = ref(null)

const searchForm = reactive({
  actionType: '',
  module: '',
  operator: '',
  dateRange: null
})

const pagination = reactive({
  current: 1,
  size: 10,
  total: 0
})

const logList = ref([])

const getModuleText = (module) => {
  const map = { user: '用户管理', debt: '债务管理', merchant: '商户管理', task: '任务管理', credit: '积分管理', transaction: '交易管理', system: '系统设置', auth: '认证' }
  return map[module] || module
}

const getModuleTag = (module) => {
  const map = { user: 'primary', debt: 'warning', merchant: 'success', task: 'info', credit: 'danger', transaction: '', system: '', auth: 'success' }
  return map[module] || ''
}

const getActionText = (action) => {
  const map = { login: '登录', logout: '登出', create: '创建', update: '更新', delete: '删除', view: '查看', audit: '审核' }
  return map[action] || action
}

const getActionTag = (action) => {
  const map = { login: 'success', logout: 'info', create: 'primary', update: 'warning', delete: 'danger', view: '', audit: 'warning' }
  return map[action] || ''
}

const formatDateTime = (dateString) => {
  if (!dateString) return ''
  return new Date(dateString).toLocaleString('zh-CN')
}

const formatJson = (str) => {
  if (!str) return ''
  try { return JSON.stringify(JSON.parse(str), null, 2) } catch (e) { return str }
}

const fetchLogs = async () => {
  try {
    loading.value = true
    const params = {
      page: pagination.current,
      size: pagination.size,
      ...searchForm
    }
    if (searchForm.dateRange && searchForm.dateRange.length === 2) {
      params.startDate = searchForm.dateRange[0]
      params.endDate = searchForm.dateRange[1]
    }
    delete params.dateRange
    
    const response = await logApi.getOperationLogs(params)
    if (response.code === 200 && response.data) {
      logList.value = response.data.list || []
      pagination.total = response.data.total || 0
    } else {
      if (response.code === 401) {
        localStorage.removeItem('admin-token')
        localStorage.removeItem('admin-info')
        window.location.href = '/#/login'
      } else {
        ElMessage.error(response.message || '获取日志列表失败')
      }
    }
  } catch (error) {
    ElMessage.error('获取日志列表异常')
  } finally {
    loading.value = false
  }
}

const refreshData = () => { fetchLogs() }
const handleSearch = () => { pagination.current = 1; fetchLogs() }
const resetSearch = () => { Object.keys(searchForm).forEach(key => { searchForm[key] = key === 'dateRange' ? null : '' }); handleSearch() }
const handleSizeChange = (size) => { pagination.size = size; fetchLogs() }
const handleCurrentChange = (page) => { pagination.current = page; fetchLogs() }
const handleViewDetail = (row) => { currentLog.value = row; detailDialogVisible.value = true }

onMounted(() => { fetchLogs() })
</script>

<style scoped>
.operation-log { padding: 20px; }
.header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
.header h2 { margin: 0; font-size: 24px; color: #ffffff; }
.actions { display: flex; gap: 10px; }
.search-card { margin-bottom: 20px; }
.pagination { margin-top: 20px; display: flex; justify-content: flex-end; }
</style>
